<template>
  <header id="TopNav">
    <div class="content">
      <img class="brand" src="/logo.svg" >
      <template v-if="theUser.isSmartEnabled">
        <a href="/insights/">
          <span>{{ $t('Insights') }}</span>
        </a>
        <router-link v-if="theUser.hasCampaignAccess" tag="a" :to="{ path: '/campaigns/' }" :class="{ on: isInCampaigns }">
          <span>{{ $t('Campaigns') }}</span>
        </router-link>
        <router-link v-if="theUser.webPushEnabled && theUser.hasNotificationAccess" tag="a" :to="{ path: '/notifications/' }" :class="{ on: isInNotifications }">
          <span>{{ $t('Notifications') }}</span>
        </router-link>
        <a v-if="theUser.hasCreativeAccess" href="/creative/">
          <span>{{ $t('Creative') }}</span>
        </a>
      </template>
    </div>

    <BackgroundProcess/>

    <Popper :zIndex="1005" auto-hide placement="bottom-end">
      <div slot="trigger" id="LanguageChooserButton" :class="{ 'sNoSuper': !theUser.isSuper && !theUser.isAgency }">
        <div :style="{ backgroundImage: defaultLanguage ? `url(https://static.fanplayr.com/portal/assets/img/flags/${getFlag(defaultLanguage)}.svg)` : '' }"></div>
      </div>
      <template #default="{hide}">
        <div id="LanguageChooser">
          <div
            v-for="lang in supportedLanguages"
            :key="lang.value"
            :value="lang.value"
            :class="{ selected: lang.value === defaultLanguage }"
            @click="chooseLanguage(lang.value, hide)"
            :style="{ backgroundImage: `url(https://static.fanplayr.com/portal/assets/img/flags/${getFlag(lang.value)}.svg)` }"
          >{{ lang.label }}</div>
          <template v-if="!Env.isProduction() && theUser.isSuper">
            <div
              value="en-debug1"
              :class="{ selected: defaultLanguage === 'en-debug1' }"
              @click="chooseLanguage('en-debug1', hide)"
            >❗"PREVIEW"</div>
            <div
              value="en-debug2"
              :class="{ selected: defaultLanguage === 'en-debug2' }"
              @click="chooseLanguage('en-debug2', hide)"
            >⋮PREVIEW⋮</div>
          </template>
        </div>
      </template>
    </Popper>

    <div v-if="theUser.isSuper || theUser.isAgency" id="SuperAccountSwitcher">
      <i class="fa fa-lg fa-users"></i>
      <select :value="accountToSwitchToId" @change="accountIdChanged">
        <option v-if="theUser.isSuper" :value="theUser.accountId">{{$t('Super Account')}}</option>
        <option v-if="theUser.isAgency" :value="theUser.accountId">{{$t('Agency Account')}}</option>
        <option value="">-------------</option>
        <option v-for="acc in agencyAccounts" :key="acc.id" :value="acc.id">{{ acc.name }}</option>
      </select>
    </div>

    <Popper :zIndex="5" auto-hide placement="bottom-end">
      <div slot="trigger" class="TopNav-dropdown-button">
        <div v-if="theUser.logoUrl" class="logo" style="background: #efefef center center no-repeat; background-size: cover;" :style="{ backgroundImage: `url('${theUser.logoUrl}')` }"></div>
        <div v-else class="logo-missing">{{ theUser.accountName && theUser.accountName.substr(0, 1).toUpperCase() }}</div>
        <span class="account-name">{{ theUser.accountName }}</span>
        <span class="caret"></span>
      </div>
      <template #default="{hide}">
        <ul class="TopNav-dropdown-list">
          <li @click="hide" class="head"><router-link tag="a" :to="{ path: '/account/profile/' }">{{ theUser.firstName }} {{ theUser.lastName }}<span>{{$t('Your Account')}}</span></router-link></li>
          <li @click="hide"><router-link tag="a" :to="{ path: '/account/profile/' }">{{$t('Account Settings')}}</router-link></li>
          <template v-if="theUser.hasTransactionAccess">
            <li @click="hide"><router-link tag="a" :to="{ path: '/account/transactions/' }">{{$t('Transactions')}}</router-link></li>
          </template>
          <li @click="hide"><router-link tag="a" :to="{ path: '/account/sharedRulesets/' }">{{$t('Shared Rulesets')}}</router-link></li>
          <li @click="hide"><router-link tag="a" :to="{ path: '/account/streams/' }">{{$t('Streams')}}</router-link></li>
          <template v-if="theUser.isAgency || theUser.isSuper">
            <li role="separator" class="divider"></li>
            <li @click="hide"><a @click="showSwitchAccounts" id="switch-account">{{$t('Switch Accounts')}}</a></li>
          </template>
          <li role="separator" class="divider"></li>
          <li @click="hide"><router-link tag="a" :to="{ path: '/redirectProtectedContent/?url=https%3A%2F%2Fprotected.fanplayr.com%2Fdocs%2Fportal%2Fcampaigns%2F01-intro.html' }" target="_blank">{{$t('Documentation')}}</router-link>
          <li @click="hide"><router-link tag="a" :to="{ path: '/support/' }">{{$t('Help & Support')}}</router-link></li>
          <li @click="hide"><router-link tag="a" :to="{ path: '/about/' }">{{$t('About')}}</router-link></li>
          <template v-if="theUser.isSuper">
            <li role="separator" class="divider"></li>
            <li @click="hide"><router-link tag="a" :to="{ path: '/translations/' }">{{$t('Translations')}}</router-link></li>
          </template>
          <li role="separator" class="divider"></li>
          <li @click="hide"><a @click="logout"><span>{{$t('Logout')}}</span></a></li>
        </ul>
      </template>
    </Popper>

  </header>
  
</template>

<script>

import Env from '../classes/utils/Env'
import BackgroundProcess from '@/components/dialogue/BackgroundProcess'

export default {
  name: 'TopNav',
  data() {
    return {
      accountToSwitchToId: this.theUser && this.theUser.accountId,
      defaultLanguage: this.theUser && this.theUser.defaultLanguage,
      refreshChannel: null,
      Env: Env
    }
  },
  created() {
    if ('BroadcastChannel' in self) {
      this.refreshChannel = new BroadcastChannel('refresh')
    }
  },
  async mounted() {
    this.accountToSwitchToId = this.theUser.onBehalfOfAccountId
    window.showSwitchAccounts = this.showSwitchAccounts

    this.defaultLanguage = this.theUser.defaultLanguage

    // set lang for embedded insights (notifications) 
    if (window.INSIGHTS_CONFIG) {
      window.INSIGHTS_CONFIG.locale = this.defaultLanguage
    }

    this.$watch('defaultLanguage', async (newVal) => {

      // set lang for embedded insights (notifications) 
      if (window.INSIGHTS_CONFIG) {
        window.INSIGHTS_CONFIG.locale = newVal
      }

      NProgress.start()
      await this.setLanguage(newVal)

      this.theUser.defaultLanguage = newVal

      // save
      await this.api.updateUserLanguage(newVal)

      window.localStorage['portal-language'] = newVal

      // tell other windows to refresh
      if (this.refreshChannel) {
        this.refreshChannel.postMessage({
          hard: false
        })
      }

      // for non dev force reload so things like insights embedded and rulesets are relaoded
      if (!Env.isDevelopment()) {
        window.location.reload()
      }

      NProgress.done()
    })

    // allows us to preview translations
    if (this.refreshChannel) {
      this.refreshChannel.onmessage = (e) => {
        window.location.reload(e.data.hard)
      }
    }
  },
  methods: {
    chooseLanguage(lang, hide) {
      this.defaultLanguage = lang
      hide()
    },
    accountIdChanged(event) {
      this.doSwitchNow(event.currentTarget.value)
    },
    async logout() {
      await this.api.logout()
      this.$router.push('/site/login/')
    },
    async showSwitchAccounts() {
      let newAccountId = await this.$dialogue.switchAccount()
      if (newAccountId) {
        this.doSwitchNow(newAccountId)
      }
    },
    async doSwitchNow(accountId) {
      NProgress.start()
      this.api.switchAccount(accountId)
      .then(async () => {
        await this.doRefreshToken()
        this.accountToSwitchToId = accountId
        const reroutes = {
          '/campaigns/': {
            to: '/campaigns/',
            strict: true
          },
          '/sessionViewer/': {
            to: '/account/transactions/'
          },
          '/userViewer/': {
            to: '/account/transactions/'
          }
        }

        let newRoute = ''
        for (let key of Object.keys(reroutes)) {
          const route = reroutes[key]
          if (route.strict) {
            if (this.$route.path.indexOf(key) === 0 && this.$route.path !== key) {
              newRoute = route.to
              break
            }
          } else {
            if (this.$route.path.indexOf(key) === 0) {
              newRoute = route.to
              break
            }
          }
        }

        if (newRoute) {
          this.$router.push(newRoute)
        } else {
          this.$router.go()
        }

        // tell other windows to refresh
        if (this.refreshChannel) {
          this.refreshChannel.postMessage({
            hard: false
          })
        }
      })
      .catch(this.apiCatch)
      .finally(() => {
        NProgress.done()
      })
    }
  },
  computed: {
    // HACK
    isInCampaigns() {
      return this.$route.path.indexOf('/campaigns') === 0
    },
    // HACK
    isInNotifications() {
      return this.$route.path.indexOf('/notifications') === 0
    }
  },
  components: {
    BackgroundProcess
  }
}
</script>

<style lang="scss">

#LanguageChooser {
  // z-index: 4;
  background: #fff;
  margin: 4px 0;
  padding-top: 10px;
  background: #fff;
  border: 1px solid #dbe0e4;
  border-radius: 3px;
  box-shadow: 0 2px 16px rgba(33, 43, 54, 0.08);
  &.site {
    padding-top: 0px;
  }
  div {
    user-select: none;
    padding: 8px 20px;
    cursor: pointer;
    padding-left: 40px;
    background-size: 20px 20px;
    background-repeat: no-repeat;
    background-position: 10px;
    &:hover {
      background-color: #f5f5f5;
    }
    &.selected {
      background-color: #f0f0f0;
    }
  }
}

#TopNav {
  display: flex;
  align-items: center;
  height: 65px;
  background: #fff;
  border-bottom: 1px solid #eee;
  color: #666;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  width: 100%;

  position: fixed;
  right: 0;
  left: 0;
  z-index: 100;
  top: 0;

  @media screen and (max-height: 900px) {
    height: 30px;
  }

  .content {
    display: inline-block;
    padding-left: 1em;
    color: #888;
    //font-weight: 300;
    font-size: 20px;
    width: 100%;

    @media screen and (max-height: 900px) {
      font-size: 18px;
    }

    a {
      display: inline-block;
      padding-left: 1.5em;
      color: #888;
      font-weight: 300;

      @media screen and (max-width: 900px) {
        padding-left: 1em;
      }

      &.on {
        color: #ff9800;
        font-weight: 300;
      }
    }

    img.brand {
      height: 30px;

      @media screen and (max-width: 850px) {
        display: none;
      }

      @media screen and (max-height: 900px) {
        height: 20px;
      }
    }

    #menuProfile {
      float: right;
    }
    #menuLogout {
      float: right;
      margin-right: 55px;
    }
  }

  #SuperAccountSwitcher {
    position: relative;
    display: flex;
    width: 40px;
    height: 100%;
    margin-right: 270px;
    align-items: center;
    justify-content: center;
    font-size: 14px;

    select {
      position: absolute;
      width: 40px;
      height: 40px;
      max-height: 100%;
      opacity: 0;
      cursor: pointer;
    }

    @media screen and (max-width: 780px) {
      display: none;
    }
    @media screen and (max-height: 900px) {
      margin-right: 265px;
      .fa {
        font-size: 1em;
      }
    }
  }

  #LanguageChooserButton {
    position: relative;
    display: flex;
    width: 40px;
    height: 100%;
    margin-right: 15px;
    align-items: center;
    justify-content: center;
    font-size: 1.5em;
    cursor: pointer;

    div {
      width: 20px;
      height: 20px;
      background-size: 20px 20px;
      background-repeat: no-repeat;
    }

    &.sNoSuper {
      margin-right: 270px;
    }

    @media screen and (max-width: 780px) {
      margin-right: 270px;
    }

    @media screen and (max-width: 720px) {
      margin-right: 110px;
    }
  }

  .TopNav-dropdown-button {
    position: absolute;
    right: 0px;
    top: 0px;
    border-left: 1px solid #efefef;
    width: 260px;
    height: 60px;
    cursor: pointer;
    background: #fff;

    @media screen and (max-height: 900px) {
      height: 30px;
    }

    @media screen and (max-width: 720px) {
      width: 100px;
    }

    > * {
      pointer-events: none;
    }

    .logo {
      display: block;
      height: 37px;
      width: 37px;
      border-radius: 25px;
      border: 1px solid #fff;
      color: white;
      position: absolute;
      top: 12px;
      left: 10px;

      @media screen and (max-height: 900px) {
        width: 20px;
        height: 20px;
        top: 5px;
        left: 4px;
      }
    }

    .logo-missing {
      color: #888;
      height: 37px;
      width: 37px;
      background: #efefef;
      background-position: 50%;
      background-repeat: no-repeat;
      background-size: cover;
      background-image: none;
      border-radius: 50%;
      box-shadow: 0 0 1px #fff, 0 1px 3px rgba(0, 0, 0, 0.05);
      text-align: center;
      line-height: 37px;
      font-size: 19px;
      position: absolute;
      top: 12px;
      left: 10px;

      @media screen and (max-height: 900px) {
        width: 20px;
        height: 20px;
        top: 5px;
        left: 4px;
        line-height: 20px;
        font-size: 14px;
      }
    }

    .account-name {
      font-weight: 300;
      color: #888;
      width: 158px;
      text-align: left;
      overflow: hidden;
      text-overflow: ellipsis;
      position: absolute;
      top: 20px;
      left: 58px;
      font-size: 20px;
      white-space: nowrap;

      @media screen and (max-height: 900px) {
        font-size: 16px;
        top: 4px;
      }

      @media screen and (max-width: 720px) {
        display: none;
      }
    }

    .caret {
      position: absolute;
      top: 0px;
      right: 0px;
      color: #fff;
      display: block;
      width: 40px;
      height: 65px;
      background: #363c4b;

      @media screen and (max-height: 900px) {
        height: 30px;
      }
    }

    .caret::after {
      content: '';
      width: 0px;
      height: 0px;
      display: block;
      position: absolute;
      border-top: 4px solid;
      border-right: 4px solid transparent;
      border-left: 4px solid transparent;
      position: absolute;
      left: 16px;
      top: 30px;

      @media screen and (max-height: 900px) {
        top: 14px;
      }
    }
  }
}
.TopNav-dropdown-list {
  display: block;
  margin: 4px 0;
  padding: 8px 0;
  background: #fff;
  border: 1px solid #dbe0e4;
  border-radius: 3px;
  box-shadow: 0 2px 16px rgba(33, 43, 54, 0.08);

  .head {
    margin-bottom: 10px;
    a {
      span {
          display: block;
          color: #ccc;
          font-size: .7em;
      }
    }
  }

  li > a {
    display: block;
    padding: 3px 20px;
    clear: both;
    font-weight: 400;
    line-height: 1.42857143;
    color: #333;
    white-space: nowrap;
    &:hover {
      color: #262626;
      text-decoration: none;
      background-color: #f5f5f5;
    }
  }

  a {
    color: #000;
    cursor: pointer;
  }

  .divider {
    height: 1px;
    margin: 9px 0;
    overflow: hidden;
    background-color: #e5e5e5;
  }
}
</style>